import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { SajuResult } from '@/utils/sajuCalculations';
import { calculateSewoon } from '@/utils/sewoonCalculations';

interface Props {
  result: SajuResult;
}

const MONTH_ABBR_EN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const SCORE_STYLES = [
  { min: 80, bg: '#F0FDF4', border: '#86EFAC', labelKey: 'monthly.score.great', labelColor: '#15803D', dotColor: '#22c55e' },
  { min: 65, bg: '#F9FAFB', border: '#D1FAE5', labelKey: 'monthly.score.good',  labelColor: '#059669', dotColor: '#6ee7b7' },
  { min: 50, bg: '#FAFAFA', border: '#E5E7EB', labelKey: 'monthly.score.normal', labelColor: '#9CA3AF', dotColor: '#d1d5db' },
  { min: 35, bg: '#FFFBEB', border: '#FDE68A', labelKey: 'monthly.score.caution', labelColor: '#B45309', dotColor: '#fbbf24' },
  { min: 0,  bg: '#FFF7F7', border: '#FECACA', labelKey: 'monthly.score.bad',   labelColor: '#DC2626', dotColor: '#f87171' },
];

function getStyle(score: number) {
  return SCORE_STYLES.find(s => score >= s.min) ?? SCORE_STYLES[SCORE_STYLES.length - 1];
}

export default function MonthlyFortuneCards({ result }: Props) {
  const { t, i18n } = useTranslation();
  const [expandedMonth, setExpandedMonth] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'all' | 'good' | 'bad'>('all');

  const lang = i18n.language === 'en' ? 'en' : 'ko';

  // Recalculate when language changes
  const sewoon = useMemo(() => calculateSewoon(result, lang), [result, lang]);
  const { monthly } = sewoon;

  const filtered = monthly.filter(m => {
    if (activeTab === 'good') return m.score >= 65;
    if (activeTab === 'bad') return m.score < 50;
    return true;
  });

  const goodMonths = monthly.filter(m => m.score >= 65).length;
  const badMonths = monthly.filter(m => m.score < 50).length;

  const getMonthLabel = (m: number) =>
    i18n.language === 'en' ? MONTH_ABBR_EN[m - 1] : `${m}${t('monthly.month.suffix')}`;

  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-5 mb-6">
      {/* 헤더 */}
      <div className="flex items-center gap-2 mb-5">
        <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-xl">
          <i className="ri-calendar-2-line text-amber-500 text-sm" />
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">{t('monthly.title.year', { year: 2026 })}</h3>
          <p className="text-xs text-gray-400">{t('monthly.subtitle')}</p>
        </div>
      </div>

      {/* 요약 통계 */}
      <div className="grid grid-cols-3 gap-2 mb-5">
        {[
          { label: t('monthly.good'), count: goodMonths, color: '#15803D', bg: '#DCFCE7', icon: 'ri-sun-line' },
          { label: t('monthly.normal'), count: 12 - goodMonths - badMonths, color: '#6B7280', bg: '#F3F4F6', icon: 'ri-cloud-line' },
          { label: t('monthly.caution'), count: badMonths, color: '#B45309', bg: '#FEF3C7', icon: 'ri-rainy-line' },
        ].map(item => (
          <div key={item.label} className="rounded-xl p-3 text-center" style={{ backgroundColor: item.bg }}>
            <div className="w-7 h-7 flex items-center justify-center mx-auto mb-1.5">
              <i className={`${item.icon} text-base`} style={{ color: item.color }} />
            </div>
            <div className="text-xl font-black" style={{ color: item.color }}>{item.count}</div>
            <div className="text-[10px] font-medium" style={{ color: item.color }}>{item.label}</div>
          </div>
        ))}
      </div>

      {/* 탭 */}
      <div className="flex gap-1.5 bg-gray-100 p-1 rounded-full mb-5">
        {[
          { id: 'all' as const, label: t('monthly.tab.all') },
          { id: 'good' as const, label: t('monthly.tab.good') },
          { id: 'bad' as const, label: t('monthly.tab.bad') },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 text-xs font-semibold py-1.5 rounded-full transition-all cursor-pointer whitespace-nowrap ${
              activeTab === tab.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* 월별 카드 목록 */}
      <div className="space-y-2">
        {filtered.map(m => {
          const style = getStyle(m.score);
          const isExpanded = expandedMonth === m.month;

          return (
            <button
              key={m.month}
              onClick={() => setExpandedMonth(isExpanded ? null : m.month)}
              className="w-full text-left rounded-2xl border-2 overflow-hidden transition-all duration-300 cursor-pointer"
              style={{ borderColor: style.border, backgroundColor: style.bg }}
            >
              {/* 기본 행 */}
              <div className="flex items-center gap-3 p-3.5">
                {/* 달 이름 */}
                <div className="w-10 flex-none text-center">
                  <div className="text-base font-black text-gray-900 leading-none">{getMonthLabel(m.month)}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">{m.ganji}</div>
                </div>

                {/* 구분선 */}
                <div className="w-px h-8 bg-gray-200 flex-none" />

                {/* 점수 바 */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-gray-600 truncate">{m.highlight}</span>
                    <div className="flex items-baseline gap-1 ml-2 flex-none">
                      <span className="text-sm font-black" style={{ color: style.labelColor }}>{m.score}</span>
                      <span className="text-[10px] font-medium text-gray-400">{t('sewoon.score')}</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${m.score}%`, backgroundColor: style.dotColor }}
                    />
                  </div>
                </div>

                {/* 상태 뱃지 */}
                <div
                  className="flex-none text-xs font-bold px-2.5 py-1 rounded-full"
                  style={{ backgroundColor: `${style.dotColor}25`, color: style.labelColor }}
                >
                  {t(style.labelKey)}
                </div>

                {/* 펼치기 화살표 */}
                <div className="w-5 h-5 flex items-center justify-center flex-none">
                  <i className={`ri-arrow-down-s-line text-gray-400 text-base transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
              </div>

              {/* 확장 영역 */}
              {isExpanded && (
                <div className="px-4 pb-4 pt-1 border-t border-gray-100">
                  <p className="text-sm text-gray-600 leading-relaxed">{m.message}</p>
                </div>
              )}
            </button>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-8">
            <div className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full mx-auto mb-2">
              <i className="ri-calendar-check-line text-gray-300 text-lg" />
            </div>
            <p className="text-xs text-gray-400">{t('monthly.empty')}</p>
          </div>
        )}
      </div>
    </div>
  );
}
